/**
 * AI Agent Pipeline Service
 *
 * The execution engine for autonomous AI actions.
 * Every AI action flows through this pipeline:
 *
 *   Intent → Propose → Validate → [Approve] → Execute → Log → Result
 *
 * This service coordinates between the AI engine, the orchestrator,
 * and the permission system. It never bypasses RBAC — the AI agent
 * is treated as a virtual org member.
 */

import type { AiContext } from '@/services/ai.service';
import * as aiService from '@/services/ai.service';
import { useAiAgentStore } from '@/store/aiAgentStore';
import { usePlatformStore } from '@/store/platformStore';
import type {
    AiAction,
    AiActionCategory,
    AiActionStep,
    AiActionType,
    AiAgentPolicy,
    AiPermission,
    AiWorkflow,
} from '@/types/ai-authority';
import { BUILT_IN_WORKFLOWS } from '@/types/ai-authority';
import type { AuditAction, LogEntry } from '@/types/platform';

/* ================================================================== */
/*  Helpers                                                           */
/* ================================================================== */

function getCategoryForAction(type: AiActionType): AiActionCategory {
  const prefix = type.split('.')[1] as AiActionCategory;
  const validCategories: AiActionCategory[] = [
    'code',
    'build',
    'deploy',
    'infra',
    'security',
    'optimize',
    'monitor',
    'workspace',
  ];
  return validCategories.includes(prefix) ? prefix : 'code';
}

function getRequiredPermissions(type: AiActionType): AiPermission[] {
  const map: Partial<Record<AiActionType, AiPermission[]>> = {
    'ai.code.create': ['code:create', 'ai:generate'],
    'ai.code.modify': ['code:modify', 'ai:generate'],
    'ai.code.delete': ['code:delete'],
    'ai.code.refactor': ['code:modify', 'ai:optimize'],
    'ai.build.compile': ['build:compile'],
    'ai.build.test': ['build:test'],
    'ai.build.fix_errors': ['code:modify', 'ai:fix'],
    'ai.deploy.testnet': ['deploy:testnet'],
    'ai.deploy.mainnet': ['deploy:mainnet'],
    'ai.deploy.verify': ['deploy:testnet'],
    'ai.infra.scale_up': ['infra:scale'],
    'ai.infra.scale_down': ['infra:scale'],
    'ai.infra.configure_rpc': ['rpc:configure'],
    'ai.security.audit': ['ai:audit', 'code:read'],
    'ai.security.patch': ['code:modify', 'ai:fix'],
    'ai.security.rotate_keys': ['secret:rotate'],
    'ai.optimize.gas': ['ai:optimize', 'code:read'],
    'ai.optimize.cost': ['ai:optimize'],
    'ai.optimize.performance': ['ai:optimize'],
    'ai.workspace.create': ['workspace:create'],
    'ai.workspace.configure': ['workspace:modify'],
    'ai.workspace.cleanup': ['workspace:modify'],
  };
  return map[type] ?? [];
}

function emitAgentLog(
  source: 'ai',
  level: 'info' | 'warn' | 'error',
  message: string,
  metadata?: Record<string, unknown>,
) {
  const entry: LogEntry = {
    id: `ai-log-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    workspaceId: 'agent',
    source,
    level,
    message: `[AI Agent] ${message}`,
    timestamp: new Date().toISOString(),
    metadata,
  };
  usePlatformStore.getState().appendLog(entry);
}

/* ================================================================== */
/*  Pipeline: Propose Action                                          */
/* ================================================================== */

/**
 * Propose an AI action. This is the entry point for all autonomous
 * AI operations. The action goes through permission checks and either
 * queues for approval or auto-executes based on tier + policy.
 */
export function proposeAction(
  type: AiActionType,
  title: string,
  description: string,
  plan: AiActionStep[],
  options: {
    projectId?: string;
    workspaceId?: string;
    affectedFiles?: string[];
    chainId?: number;
    estimatedCost?: { gas?: number; usd?: number };
  } = {},
): { action: AiAction | null; error?: string } {
  const store = useAiAgentStore.getState();

  // Pre-flight permission check
  const canExecute = store.canExecuteAction(type);
  if (!canExecute.allowed) {
    emitAgentLog('ai', 'warn', `Action blocked: ${type} — ${canExecute.reason}`);
    return { action: null, error: canExecute.reason };
  }

  const action = store.proposeAction({
    type,
    category: getCategoryForAction(type),
    title,
    description,
    requiredPermissions: getRequiredPermissions(type),
    plan,
    ...options,
    rollback: {
      canRollback: [
        'ai.code.create',
        'ai.code.modify',
        'ai.code.delete',
        'ai.code.refactor',
      ].includes(type),
    },
  });

  emitAgentLog('ai', 'info', `Action proposed: ${title} [${type}] → ${action.status}`, {
    actionId: action.id,
    risk: action.risk,
    autoApproved: action.autoApproved,
  });

  // If auto-approved, kick off execution
  if (action.autoApproved) {
    executeAction(action.id).catch((err) => {
      emitAgentLog('ai', 'error', `Auto-execution failed: ${err}`);
    });
  }

  return { action };
}

/* ================================================================== */
/*  Pipeline: Execute Action                                          */
/* ================================================================== */

/**
 * Execute an approved action. Runs each step sequentially,
 * logging progress and handling failures.
 */
export async function executeAction(actionId: string): Promise<void> {
  const store = useAiAgentStore.getState();

  const action = store.getActionById(actionId);
  if (!action) {
    emitAgentLog('ai', 'error', `Cannot execute: action ${actionId} not found`);
    return;
  }

  if (action.status !== 'approved') {
    emitAgentLog(
      'ai',
      'warn',
      `Cannot execute action ${actionId}: status is ${action.status}, not approved`,
    );
    return;
  }

  // Final permission check
  const canExecute = store.canExecuteAction(action.type);
  if (!canExecute.allowed) {
    store.failAction(actionId, [canExecute.reason ?? 'Permission denied']);
    return;
  }

  store.startExecution(actionId);
  emitAgentLog('ai', 'info', `Executing: ${action.title}`, { actionId, steps: action.plan.length });

  const startTime = Date.now();

  try {
    for (const step of action.plan) {
      emitAgentLog('ai', 'info', `Step ${step.order}: ${step.description}`);

      try {
        const result = await executeStep(step, action);
        step.status = 'completed';
        step.result = result;
      } catch (err) {
        step.status = 'failed';
        step.error = err instanceof Error ? err.message : String(err);
        emitAgentLog('ai', 'error', `Step ${step.order} failed: ${step.error}`);

        // Fail the entire action
        store.failAction(actionId, [
          `Step ${step.order} (${step.description}) failed: ${step.error}`,
        ]);
        return;
      }
    }

    // All steps passed
    const durationMs = Date.now() - startTime;
    store.completeAction(actionId, {
      success: true,
      summary: `Completed: ${action.title} (${durationMs}ms)`,
      outputs: collectStepOutputs(action.plan),
      durationMs,
    });

    emitAgentLog('ai', 'info', `Completed: ${action.title} in ${durationMs}ms`, { actionId });

    // Append to platform audit log
    usePlatformStore.getState().appendAuditEntry({
      id: `audit-ai-${Date.now()}`,
      orgId: usePlatformStore.getState().activeOrg?.id ?? '',
      userId: 'ai-agent',
      userEmail: 'ai@kubernodes.internal',
      action: mapToAuditAction(action.type),
      resource: action.type,
      resourceId: action.id,
      metadata: {
        title: action.title,
        risk: action.risk,
        durationMs,
        autoApproved: action.autoApproved,
      },
      ipAddress: 'internal',
      timestamp: new Date().toISOString(),
    });
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err);
    store.failAction(actionId, [errorMsg]);
    emitAgentLog('ai', 'error', `Execution failed: ${errorMsg}`, { actionId });
  }
}

/* ================================================================== */
/*  Step Executor                                                     */
/* ================================================================== */

async function executeStep(step: AiActionStep, action: AiAction): Promise<unknown> {
  const ctx: AiContext = {
    projectId: action.projectId,
  };

  switch (step.service) {
    case 'ai': {
      switch (step.method) {
        case 'generate':
          return aiService.generate(step.params.prompt as string, ctx);
        case 'audit':
          return aiService.audit(step.params.code as string, ctx);
        case 'fix':
          return aiService.fix(step.params.code as string, step.params.error as string, ctx);
        case 'optimize':
          return aiService.optimize(step.params.code as string, ctx);
        case 'explain':
          return aiService.explain(step.params.code as string, ctx);
        case 'test':
          return aiService.generateTests(step.params.code as string, ctx);
        default:
          throw new Error(`Unknown AI method: ${step.method}`);
      }
    }

    case 'file': {
      // File operations would go through workspace service
      emitAgentLog('ai', 'info', `File operation: ${step.method} — ${JSON.stringify(step.params)}`);
      return { ok: true, method: step.method, params: step.params };
    }

    case 'deploy': {
      emitAgentLog(
        'ai',
        'info',
        `Deploy operation: ${step.method}`,
        step.params as Record<string, unknown>,
      );
      return { ok: true, method: step.method, params: step.params };
    }

    case 'workspace': {
      emitAgentLog(
        'ai',
        'info',
        `Workspace operation: ${step.method}`,
        step.params as Record<string, unknown>,
      );
      return { ok: true, method: step.method, params: step.params };
    }

    case 'infra': {
      emitAgentLog(
        'ai',
        'info',
        `Infra operation: ${step.method}`,
        step.params as Record<string, unknown>,
      );
      return { ok: true, method: step.method, params: step.params };
    }

    case 'rpc': {
      emitAgentLog(
        'ai',
        'info',
        `RPC operation: ${step.method}`,
        step.params as Record<string, unknown>,
      );
      return { ok: true, method: step.method, params: step.params };
    }

    case 'secret': {
      emitAgentLog('ai', 'info', `Secret operation: ${step.method}`, { key: step.params.key });
      return { ok: true, method: step.method };
    }

    default:
      throw new Error(`Unknown service: ${step.service}`);
  }
}

function collectStepOutputs(steps: AiActionStep[]): Record<string, unknown> {
  const outputs: Record<string, unknown> = {};
  for (const step of steps) {
    if (step.result) {
      outputs[`step_${step.order}`] = step.result;
    }
  }
  return outputs;
}

function mapToAuditAction(type: AiActionType): AuditAction {
  // Map AI actions to closest platform audit actions
  if (type.startsWith('ai.deploy')) return 'deploy.testnet';
  if (type.startsWith('ai.workspace')) return 'workspace.create';
  if (type.startsWith('ai.security.rotate')) return 'secret.update';
  return 'settings.update';
}

/* ================================================================== */
/*  Workflow Executor                                                 */
/* ================================================================== */

/**
 * Execute a predefined workflow as a compound action.
 */
export function startWorkflow(
  workflowId: string,
  params: {
    prompt?: string;
    code?: string;
    projectId?: string;
    workspaceId?: string;
    chainId?: number;
  } = {},
): { action: AiAction | null; error?: string } {
  const workflow = BUILT_IN_WORKFLOWS.find((w) => w.id === workflowId);
  if (!workflow) {
    return { action: null, error: `Unknown workflow: ${workflowId}` };
  }

  const store = useAiAgentStore.getState();

  // Check tier
  if (store.policy.tier < workflow.minTier) {
    return {
      action: null,
      error: `Workflow "${workflow.name}" requires tier ${workflow.minTier}+, current tier: ${store.policy.tier}`,
    };
  }

  // Build steps from workflow actions
  const steps: AiActionStep[] = workflow.actions.map((actionType, i) => ({
    id: `step-${i}`,
    order: i + 1,
    description: actionType.replace(/^ai\./, '').replace(/[._]/g, ' '),
    service: getServiceForAction(actionType),
    method: getMethodForAction(actionType),
    params: buildStepParams(actionType, params),
    status: 'pending' as const,
  }));

  // Use the first action type for the proposal (highest level)
  const primaryType = workflow.actions[workflow.actions.length - 1];
  return proposeAction(primaryType, `Workflow: ${workflow.name}`, workflow.description, steps, {
    projectId: params.projectId,
    workspaceId: params.workspaceId,
    chainId: params.chainId,
  });
}

function getServiceForAction(type: AiActionType): AiActionStep['service'] {
  if (
    type.startsWith('ai.code') ||
    type.startsWith('ai.optimize') ||
    type.startsWith('ai.security.audit')
  )
    return 'ai';
  if (type.startsWith('ai.build')) return 'ai';
  if (type.startsWith('ai.deploy')) return 'deploy';
  if (type.startsWith('ai.infra')) return 'infra';
  if (type.startsWith('ai.security.rotate')) return 'secret';
  if (type.startsWith('ai.workspace')) return 'workspace';
  return 'ai';
}

function getMethodForAction(type: AiActionType): string {
  const parts = type.split('.');
  return parts[parts.length - 1];
}

function buildStepParams(
  type: AiActionType,
  input: { prompt?: string; code?: string },
): Record<string, unknown> {
  if (type.includes('code.create') || type.includes('code.refactor')) {
    return { prompt: input.prompt ?? '' };
  }
  if (
    type.includes('audit') ||
    type.includes('optimize') ||
    type.includes('fix') ||
    type.includes('test')
  ) {
    return { code: input.code ?? '' };
  }
  return {};
}

/* ================================================================== */
/*  Policy Management                                                 */
/* ================================================================== */

export function updateAgentPolicy(patch: Partial<AiAgentPolicy>): void {
  useAiAgentStore.getState().updatePolicy(patch);
  emitAgentLog('ai', 'info', `Agent policy updated: ${Object.keys(patch).join(', ')}`);
}

export function engageKillSwitch(): void {
  useAiAgentStore.getState().engageKillSwitch();
  emitAgentLog('ai', 'warn', 'KILL SWITCH ENGAGED — All AI agent actions are now blocked');
}

export function disengageKillSwitch(): void {
  useAiAgentStore.getState().disengageKillSwitch();
  emitAgentLog('ai', 'info', 'Kill switch disengaged — Agent is now active');
}

/* ================================================================== */
/*  Approval helpers                                                  */
/* ================================================================== */

export function approveAction(actionId: string, approvedBy: string, note?: string): void {
  useAiAgentStore.getState().approveAction(actionId, approvedBy, note);
  emitAgentLog('ai', 'info', `Action approved: ${actionId} by ${approvedBy}`);

  // Auto-execute after approval
  executeAction(actionId).catch((err) => {
    emitAgentLog('ai', 'error', `Post-approval execution failed: ${err}`);
  });
}

export function rejectAction(actionId: string, note?: string): void {
  useAiAgentStore.getState().rejectAction(actionId, note);
  emitAgentLog('ai', 'info', `Action rejected: ${actionId}`);
}

export function rollbackAction(actionId: string): void {
  useAiAgentStore.getState().rollbackAction(actionId);
  emitAgentLog('ai', 'warn', `Action rolled back: ${actionId}`);
}

/* ================================================================== */
/*  Query helpers                                                     */
/* ================================================================== */

export function getAvailableWorkflows(): AiWorkflow[] {
  const tier = useAiAgentStore.getState().policy.tier;
  return BUILT_IN_WORKFLOWS.filter((w) => w.minTier <= tier);
}

export function getAgentStatus() {
  const store = useAiAgentStore.getState();
  return {
    isActive: !store.policy.killSwitch,
    tier: store.policy.tier,
    pendingCount: store.pendingActions.length,
    executingCount: store.executingActions.length,
    actionsThisHour: store.actionsThisHour,
    maxActionsPerHour: store.policy.maxActionsPerHour,
    spentToday: store.spentToday,
    maxDailyBudget: store.policy.maxDailyBudget,
    permissions: store.getEffectivePermissions(),
  };
}
